import { isValidRoomCode, normalizeRoomCode } from "@poppycock/shared";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import {
  BrandMark,
  Button,
  Card,
  ErrorBanner,
  Input,
  Label,
} from "./ui";

function codeFromUrl(): string {
  if (typeof window === "undefined") return "";
  const raw = new URLSearchParams(window.location.search).get("code");
  if (!raw) return "";
  return normalizeRoomCode(raw).slice(0, 4);
}

export function HomeScreen({
  onCreate,
  onJoin,
  error,
  onDismissError,
  connecting,
}: {
  onCreate: (name: string) => void;
  onJoin: (code: string, name: string) => void;
  error?: string | null;
  onDismissError?: () => void;
  connecting?: boolean;
}) {
  const initialCode = codeFromUrl();
  const [mode, setMode] = useState<"create" | "join">(
    initialCode ? "join" : "create",
  );
  const [name, setName] = useState("");
  const [code, setCode] = useState(initialCode);

  // Invite deep-link: /?code=ABCD
  useEffect(() => {
    const fromUrl = codeFromUrl();
    if (fromUrl && isValidRoomCode(fromUrl)) {
      setMode("join");
      setCode(fromUrl);
    }
  }, []);

  const canCreate = name.trim().length > 0 && !connecting;
  const canJoin =
    name.trim().length > 0 && isValidRoomCode(code) && !connecting;

  return (
    <div className="flex flex-1 flex-col justify-center">
      <BrandMark />

      {error && (
        <ErrorBanner message={error} onDismiss={onDismissError} />
      )}

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="mt-2"
      >
        <Card strong>
          <div className="mb-5 flex rounded-xl bg-black/30 p-1">
            <Tab
              active={mode === "create"}
              onClick={() => setMode("create")}
              label="Create room"
            />
            <Tab
              active={mode === "join"}
              onClick={() => setMode("join")}
              label="Join room"
            />
          </div>

          <div className="mb-4">
            <Label>Your name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Something ridiculous"
              maxLength={20}
              autoComplete="nickname"
              autoFocus
            />
          </div>

          {mode === "join" && (
            <div className="mb-4">
              <Label>Room code</Label>
              <Input
                value={code}
                onChange={(e) =>
                  setCode(normalizeRoomCode(e.target.value).slice(0, 4))
                }
                placeholder="ABCD"
                maxLength={4}
                className="font-mono text-lg tracking-[0.35em] uppercase"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
              />
            </div>
          )}

          {mode === "create" ? (
            <Button
              className="w-full"
              disabled={!canCreate}
              onClick={() => onCreate(name.trim())}
            >
              {connecting ? "Connecting…" : "Create room"}
            </Button>
          ) : (
            <Button
              className="w-full"
              disabled={!canJoin}
              onClick={() => onJoin(normalizeRoomCode(code), name.trim())}
            >
              {connecting ? "Connecting…" : "Join room"}
            </Button>
          )}
        </Card>
      </motion.div>

      <p className="mt-8 text-center text-xs text-[var(--color-muted)]">
        Ephemeral by design. Join mid-game anytime. When the room empties, it
        vanishes.
      </p>
    </div>
  );
}

function Tab({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 rounded-lg px-3 py-2 text-sm font-medium transition ${
        active
          ? "bg-white/10 text-[var(--color-cream)] shadow-sm"
          : "text-[var(--color-muted)] hover:text-[var(--color-cream)]"
      }`}
    >
      {label}
    </button>
  );
}
